import { useProcedureListStore } from "@/frontend/store/ProcedureListStore";
import { I18n } from "@/i18n/I18n";
import { useShallow } from "zustand/react/shallow";
import { useRequirementsStore } from "@/frontend/store/RequirementsStore";
import { RequirementProcedureTemplateAssignmentParams } from "@/backend/access/requirementProcedureTemplateAssignment/types";
import { AssignProcedureModal } from "./AssignProcedureModal";
import { GeneratedProcedure } from "../procedures/GeneratedProcedure";
import { LoadingSpinner } from "../LoadingSpinner";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { RequirementParams } from "@/backend/access/requirement/types";

interface RequirementProcedureAssignmentsLoaderProps {
  requirement: RequirementParams;
  projectId: string;
}

export function RequirementProcedureAssignmentsLoader({ requirement, projectId }: RequirementProcedureAssignmentsLoaderProps) {
  const [loading, setLoading] = useState(false);
  const readAllGeneratedProceduresForProject = useProcedureListStore(state => state.readAllGeneratedProceduresForProject);

  useEffect(() => {
    setLoading(true);
    void readAllGeneratedProceduresForProject(projectId).finally(() => {
      setLoading(false);
    });
  }, [requirement.id]);

  return (
    <div className="list-card">
      <p className="list-card-title"> {I18n.get('assignedProcedures')}</p>
      {loading ? <LoadingSpinner /> : <RequirementProcedureAssignments requirement={requirement} />}
    </div>
  );
}

interface RequirementProcedureAssignmentsProps {
  requirement: RequirementParams;
}

function RequirementProcedureAssignments({ requirement }: RequirementProcedureAssignmentsProps) {
  const allAssignments = useRequirementsStore(useShallow(state => state.assignmentsByRequirement[requirement.id] || []));
  const procedureIds = useProcedureListStore(useShallow(state => state.procedures.map(p => p.id)));

  // An assignment can outlive the procedure it points at when that procedure is
  // deleted elsewhere in the session. Those rows have nothing to render, so drop
  // them here instead of leaving empty entries behind and reporting the
  // requirement as having assignments.
  const knownProcedureIds = new Set(procedureIds);
  const assignments = allAssignments.filter(a => knownProcedureIds.has(a.procedureInstanceId));
  const assignedProcedureIds = assignments.map(a => a.procedureInstanceId);

  if (assignments.length === 0) {
    return (
      <div className="empty-list-card-content">
        <>
          <AssignProcedureModal
            assignedIds={assignedProcedureIds}
            requirementId={requirement.id}
          />
          <p className="list-card-help-text">
            {I18n.get('assignAProcedureToThisRequirement')}
          </p>
        </>
      </div>
    );
  }

  return (
    <div className="attachment-items-container">
      {assignments.map(assignment => (
        <AssignedItem key={assignment.id} assignment={assignment} requirement={requirement} />
      ))}
      <div>
        <AssignProcedureModal
          assignedIds={assignedProcedureIds}
          requirementId={requirement.id}
        />
      </div>
    </div>

  );
}

function AssignedItem(props: { assignment: RequirementProcedureTemplateAssignmentParams, requirement: RequirementParams }) {
  const { assignment, requirement } = props;

  const generatedProcedure = useProcedureListStore(useShallow(state => state.procedures.find(p => p.id === assignment.procedureInstanceId)));
  const deleteAssignment = useRequirementsStore(state => state.deleteAssignment);

  if(!generatedProcedure) return null;

  return (
    <div>
      <GeneratedProcedure key={generatedProcedure.id} procedure={generatedProcedure}>
        <button 
          className="btn" 
          onClick={() => void deleteAssignment(requirement.id, assignment.id)}
          aria-label={I18n.get('unassignProcedure', { procedure: generatedProcedure.title })}
        >
          <X color="var(--neutral-600)" />
        </button>
      </GeneratedProcedure>
    </div>
  );
}
